import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Instagram, Facebook, Share2, RefreshCw, Copy, Download, CheckCircle2,
    Link2, Sparkles, Trash2, X, Eye, PenLine, Send, AlertTriangle, Globe,
    Wand2, ImagePlus, Check,
} from "lucide-react";
import {
    fetchSocialPackages, generateSocialContent, fetchSocialContent, updateSocialContent,
    regenerateSocialCaption, markSocialReady, deleteSocialContent, fetchMetaStatus,
    fetchMetaConnectUrl, publishSocialContent, disconnectSocialPage, formatApiError,
} from "@/lib/api";
import { ArtDirectorPanel } from "./ArtDirectorPanel";
import { MasterTemplatePanel } from "./MasterTemplatePanel";

const FORMAT_LABEL = { story: "Story 1080×1920", square: "Instagram 1080×1080", facebook: "Facebook 1200×630" };
const DEST_LABEL = { fb_page: "Facebook Page", ig_feed: "Instagram Feed", ig_story: "Instagram Story", ig_reels: "Instagram Reels" };
const DEST_STATE_STYLE = {
    not_published: "bg-stone-100 text-stone-500",
    ready_to_publish: "bg-blue-100 text-blue-700",
    publishing: "bg-amber-100 text-amber-700 animate-pulse",
    published: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-600",
};
const DEST_STATE_LABEL = {
    not_published: "Not published", ready_to_publish: "Ready to publish",
    publishing: "Publishing…", published: "Published", failed: "Failed",
};

const copy = async (text, what) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
};

// ----------------------------- create panel -----------------------------
function CreatePanel({ token, onCreated }) {
    const [pkgs, setPkgs] = useState(null);
    const [slug, setSlug] = useState("");
    const [formats, setFormats] = useState(["story", "square", "facebook"]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchSocialPackages(token).then((d) => setPkgs(d.packages)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    }, [token]);

    const generate = async () => {
        if (!slug) return toast.error("Pick a tour package first");
        if (!formats.length) return toast.error("Pick at least one creative format");
        setBusy(true);
        try {
            const r = await generateSocialContent(token, { package_slug: slug, formats });
            toast.success("Creatives + AI caption generated");
            onCreated(r.content);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="social-create">
            <p className="text-sm text-stone-500 mb-4">Pick a tour package — the same branded templates and AI captions as the Share feature are used, saved into your content library.</p>
            {!pkgs ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[46vh] overflow-y-auto pr-1" data-testid="social-package-grid">
                    {pkgs.map((p) => (
                        <button key={p.slug} onClick={() => setSlug(p.slug)} data-testid={`social-pkg-${p.slug}`}
                            className={`text-left rounded-xl border overflow-hidden bg-white transition-all ${slug === p.slug ? "border-saffron ring-2 ring-saffron/40" : "border-stone-200 hover:border-saffron/60"}`}>
                            <div className="h-20 bg-stone-100">{p.cover_image && <img src={p.cover_image} alt="" className="h-full w-full object-cover" loading="lazy" />}</div>
                            <p className="p-2 text-xs font-bold text-navy leading-snug line-clamp-2">{p.name}</p>
                        </button>
                    ))}
                </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                {Object.entries(FORMAT_LABEL).map(([k, label]) => (
                    <label key={k} className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-bold cursor-pointer ${formats.includes(k) ? "border-saffron bg-gold/15 text-navy" : "border-stone-300 text-stone-500"}`}
                        data-testid={`social-format-${k}`}>
                        <input type="checkbox" className="hidden" checked={formats.includes(k)}
                            onChange={(e) => setFormats(e.target.checked ? [...formats, k] : formats.filter((f) => f !== k))} />
                        {label}
                    </label>
                ))}
                <button onClick={generate} disabled={busy || !slug} data-testid="social-generate-btn"
                    className="ml-auto rounded-full bg-saffron hover:bg-navy text-white font-display font-black text-sm px-6 py-2.5 disabled:opacity-50 inline-flex items-center gap-2 transition-colors">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating (AI caption ~15s)…</> : <><Sparkles className="h-4 w-4" /> Generate content</>}
                </button>
            </div>
        </div>
    );
}

// ----------------------------- detail modal -----------------------------
function DetailModal({ token, item, onClose, onChanged, metaStatus }) {
    const [c, setC] = useState(item);
    const [fmt, setFmt] = useState(Object.keys(item.formats)[0]);
    const [caption, setCaption] = useState(item.caption);
    const [tags, setTags] = useState(item.hashtags.join(", "));
    const [busy, setBusy] = useState("");
    const [dirty, setDirty] = useState(false);

    const save = async () => {
        setBusy("save");
        try {
            const r = await updateSocialContent(token, c.id, {
                caption, hashtags: tags.split(",").map((t) => t.trim()).filter(Boolean) });
            setC(r.content); setDirty(false); onChanged(r.content);
            toast.success("Saved — tour package data untouched");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };
    const regen = async () => {
        setBusy("regen");
        try {
            const r = await regenerateSocialCaption(token, c.id);
            setC(r.content); setCaption(r.content.caption); setTags(r.content.hashtags.join(", "));
            setDirty(false); onChanged(r.content);
            toast.success("New AI caption generated");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };
    const toggleReady = async () => {
        setBusy("ready");
        try {
            const r = await markSocialReady(token, c.id, !c.ready);
            setC(r.content); onChanged(r.content);
            toast.success(r.content.ready ? "Marked Ready to Publish" : "Moved back to draft");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };
    const publish = async (dest) => {
        setBusy(`pub-${dest}`);
        try {
            const r = await publishSocialContent(token, c.id, { destination: dest });
            if (r.content) { setC(r.content); onChanged(r.content); }
            if (r.status === "published") toast.success(`${DEST_LABEL[dest]}: published (id ${r.remote_id})`);
            else if (r.status === "failed") toast.error(`${DEST_LABEL[dest]} failed: ${r.error}`);
            else toast.info(`${DEST_LABEL[dest]}: ${r.status}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };
    const download = () => {
        const a = document.createElement("a");
        a.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/social/content/${c.id}/creative/${fmt}`;
        // needs the auth header — use a hidden form fetch instead:
        fetch(a.href, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((b) => {
                const url = URL.createObjectURL(b);
                const link = document.createElement("a");
                link.href = url; link.download = `gc-${c.package_slug}-${fmt}.png`; link.click();
                URL.revokeObjectURL(url);
            });
    };
    const remove = async () => {
        if (!window.confirm("Delete this content set? Creatives are removed permanently.")) return;
        try { await deleteSocialContent(token, c.id); toast.success("Deleted"); onChanged(null); onClose(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="social-detail-modal">
            <div className="w-full max-w-5xl bg-cream rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-navy text-cream px-5 py-3.5 flex items-center justify-between z-10 rounded-t-2xl">
                    <p className="font-display font-black">{c.package_name}</p>
                    <button onClick={onClose} data-testid="social-detail-close" className="text-cream/70 hover:text-cream"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 grid lg:grid-cols-[320px_1fr] gap-5">
                    {/* preview */}
                    <div>
                        <div className="flex gap-1.5 mb-2">
                            {Object.keys(c.formats).map((f) => (
                                <button key={f} onClick={() => setFmt(f)} data-testid={`preview-fmt-${f}`}
                                    className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${fmt === f ? "bg-navy text-white" : "bg-white border border-stone-300 text-stone-500"}`}>
                                    {FORMAT_LABEL[f].split(" ")[0]}
                                </button>
                            ))}
                        </div>
                        <div className="rounded-xl overflow-hidden bg-black border border-stone-200 flex items-center justify-center">
                            <img src={c.formats[fmt]?.preview_url} alt={FORMAT_LABEL[fmt]} data-testid="social-creative-preview"
                                className={`w-full object-contain ${fmt === "story" ? "max-h-[380px]" : ""}`} />
                        </div>
                        <p className="mt-1.5 text-[11px] text-stone-400 text-center">{FORMAT_LABEL[fmt]}</p>
                        <button onClick={download} data-testid="social-download"
                            className="mt-2 w-full rounded-full border-2 border-navy text-navy text-xs font-black uppercase tracking-wider py-2 hover:bg-navy hover:text-white transition-colors inline-flex items-center justify-center gap-1.5">
                            <Download className="h-3.5 w-3.5" /> Download PNG
                        </button>
                    </div>
                    {/* text + publish */}
                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-stone-500">Caption {c.ai_caption && <span className="text-saffron">· AI</span>}</label>
                                <button onClick={regen} disabled={busy === "regen"} data-testid="social-regen"
                                    className="text-[11px] font-bold text-saffron hover:underline inline-flex items-center gap-1">
                                    {busy === "regen" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate
                                </button>
                            </div>
                            <textarea value={caption} rows={7} data-testid="social-caption-edit"
                                onChange={(e) => { setCaption(e.target.value); setDirty(true); }}
                                className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-stone-500 mb-1.5">Hashtags (comma separated)</label>
                            <input value={tags} data-testid="social-hashtags-edit"
                                onChange={(e) => { setTags(e.target.value); setDirty(true); }}
                                className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={save} disabled={!dirty || busy === "save"} data-testid="social-save"
                                className="rounded-full bg-navy text-white text-xs font-black uppercase tracking-wider px-5 py-2 disabled:opacity-40 hover:bg-saffron transition-colors">
                                {busy === "save" ? "Saving…" : dirty ? "Save changes" : "Saved"}
                            </button>
                            <button onClick={() => copy(caption, "Caption")} data-testid="social-copy-caption"
                                className="rounded-full border border-stone-300 text-navy text-xs font-black uppercase tracking-wider px-4 py-2 hover:border-saffron inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Caption</button>
                            <button onClick={() => copy(tags, "Hashtags")} data-testid="social-copy-tags"
                                className="rounded-full border border-stone-300 text-navy text-xs font-black uppercase tracking-wider px-4 py-2 hover:border-saffron inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Hashtags</button>
                            <button onClick={() => copy(c.package_link, "Package link")} data-testid="social-copy-link"
                                className="rounded-full border border-stone-300 text-navy text-xs font-black uppercase tracking-wider px-4 py-2 hover:border-saffron inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> Link</button>
                            <button onClick={toggleReady} disabled={busy === "ready"} data-testid="social-ready-toggle"
                                className={`rounded-full text-xs font-black uppercase tracking-wider px-4 py-2 inline-flex items-center gap-1 transition-colors ${c.ready ? "bg-green-600 text-white" : "border border-green-600 text-green-700 hover:bg-green-50"}`}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> {c.ready ? "Ready to publish" : "Mark ready to publish"}
                            </button>
                            <button onClick={remove} data-testid="social-delete"
                                className="rounded-full border border-red-200 text-red-600 text-xs font-black uppercase tracking-wider px-4 py-2 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /></button>
                        </div>
                        {/* publish destinations */}
                        <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="social-publish-box">
                            <p className="text-[10px] font-black uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5"><Send className="h-3 w-3 text-saffron" /> Publish to</p>
                            <div className="space-y-2">
                                {Object.entries(DEST_LABEL).map(([key, label]) => {
                                    const st = c.destinations?.[key] || { state: "not_published" };
                                    const eligible = metaStatus?.destinations?.[key];
                                    const canTry = eligible?.ok;
                                    return (
                                        <div key={key} className="flex items-center gap-3 flex-wrap" data-testid={`dest-row-${key}`}>
                                            <span className="w-32 text-sm font-bold text-navy inline-flex items-center gap-1.5">
                                                {key === "fb_page" ? <Facebook className="h-3.5 w-3.5 text-blue-700" /> : <Instagram className="h-3.5 w-3.5 text-pink-600" />}
                                                {label}
                                            </span>
                                            <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${DEST_STATE_STYLE[st.state]}`}
                                                data-testid={`dest-state-${key}`}>{DEST_STATE_LABEL[st.state]}</span>
                                            {st.state === "failed" && st.error && <span className="text-[11px] text-red-500 max-w-[220px] truncate" title={st.error}>{st.error}</span>}
                                            <button onClick={() => publish(key)} disabled={!canTry || busy === `pub-${key}` || st.state === "publishing"}
                                                data-testid={`publish-${key}`}
                                                className="ml-auto rounded-full bg-saffron text-white text-[10px] font-black uppercase tracking-wider px-4 py-1.5 disabled:opacity-40 hover:bg-navy transition-colors inline-flex items-center gap-1">
                                                {busy === `pub-${key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                                {st.state === "failed" ? "Retry" : st.state === "published" ? "Republish" : "Publish"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            {!metaStatus?.configured && (
                                <p className="mt-3 rounded-lg bg-gold/15 border border-gold/60 px-3 py-2 text-[11px] text-navy flex gap-1.5" data-testid="publish-not-configured">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-saffron" />
                                    Automated publishing needs your Meta app connected — see the Connections tab. Nothing posts until then.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----------------------------- connections -----------------------------
function ConnectionsPanel({ token }) {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const load = useCallback(() => fetchMetaStatus(token).then(setStatus)
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [token]);
    useEffect(() => {
        load();
        const p = new URLSearchParams(window.location.search);
        if (p.get("meta") === "connected") toast.success(`Meta connected — ${p.get("pages") || ""} page(s) linked`);
        if (p.get("meta") === "error") toast.error(`Meta connection failed (${p.get("reason")})`);
    }, [load]);

    const connect = async () => {
        setBusy(true);
        try {
            const r = await fetchMetaConnectUrl(token);
            window.location.assign(r.authorize_url);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
    };

    const removePage = async (pageId) => {
        setBusy(true);
        try {
            const r = await disconnectSocialPage(token, pageId);
            toast.success(`Removed ${r.removed || "page"} — publishing now uses your remaining page(s)`);
            setConfirmId(null);
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        setBusy(false);
    };

    if (!status) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;
    return (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="social-connections">
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <p className="font-display font-black text-navy mb-3 flex items-center gap-2"><Globe className="h-4 w-4 text-saffron" /> Meta connection</p>
                {!status.configured ? (
                    <div className="rounded-xl bg-gold/15 border border-gold/60 p-4 text-sm text-navy" data-testid="meta-not-configured">
                        <p className="font-bold mb-1">Setup required — your Meta developer app</p>
                        <ol className="list-decimal ml-4 space-y-1 text-[13px]">
                            <li>In your existing Meta app (the WhatsApp one), add the <b>Facebook Login</b> and <b>Instagram Graph API</b> products — don't touch the WhatsApp settings.</li>
                            <li>Add this exact redirect URI there: <code className="bg-white px-1.5 py-0.5 rounded text-[11px] break-all">{status.redirect_uri}</code></li>
                            <li>Add <b>FACEBOOK_APP_ID</b> and <b>FACEBOOK_APP_SECRET</b> to the server Secrets.</li>
                            <li>Come back here and press Connect — you'll log in on facebook.com (never inside this app).</li>
                        </ol>
                    </div>
                ) : (
                    <button onClick={connect} disabled={busy} data-testid="meta-connect-btn"
                        className="w-full rounded-xl bg-[#1877F2] text-white font-display font-black py-3 hover:brightness-95 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Facebook className="h-4 w-4" />} Connect Facebook Pages & Instagram
                    </button>
                )}
                {status.pages.length > 0 && (
                    <div className="mt-4 space-y-2" data-testid="meta-pages">
                        {status.pages.map((p) => (
                            <div key={p.page_id} className="rounded-xl border border-stone-200 px-4 py-3 text-sm flex items-center justify-between">
                                <div>
                                    <p className="font-bold text-navy">{p.page_name}</p>
                                    <p className="text-xs text-stone-500">{p.ig_username ? `IG: @${p.ig_username}` : "No linked Instagram professional account"}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    {confirmId === p.page_id ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <button onClick={() => removePage(p.page_id)} disabled={busy}
                                                data-testid={`page-remove-confirm-${p.page_id}`}
                                                className="rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 hover:bg-red-700 disabled:opacity-50">
                                                Confirm remove
                                            </button>
                                            <button onClick={() => setConfirmId(null)} data-testid={`page-remove-cancel-${p.page_id}`}
                                                className="rounded-full border border-stone-300 text-stone-500 p-1.5 hover:bg-stone-100" title="Keep this page">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    ) : (
                                        <button onClick={() => setConfirmId(p.page_id)} data-testid={`page-remove-${p.page_id}`}
                                            className="rounded-full border border-stone-300 text-stone-400 p-1.5 hover:border-red-400 hover:text-red-600 transition-colors"
                                            title="Remove this page from the site">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <p className="font-display font-black text-navy mb-3">Destination readiness</p>
                <div className="space-y-2">
                    {Object.entries(DEST_LABEL).map(([key, label]) => (
                        <div key={key} className="flex items-start gap-2.5 text-sm" data-testid={`conn-dest-${key}`}>
                            {status.destinations[key].ok
                                ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                                : <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                            <div>
                                <p className="font-bold text-navy">{label}</p>
                                {!status.destinations[key].ok && <p className="text-xs text-stone-500">{status.destinations[key].reason}</p>}
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mt-4 text-[11px] text-stone-400">WhatsApp messaging stays exactly as it is — this connection only adds Page/Instagram publishing. Tokens are stored encrypted on the server, never in the browser.</p>
            </div>
        </div>
    );
}

// ----------------------------- main -----------------------------
export function SocialMediaManager({ token }) {
    const [tab, setTab] = useState("library");
    const [items, setItems] = useState(null);
    const [filters, setFilters] = useState({ package: "", format: "", status: "", q: "" });
    const [active, setActive] = useState(null);
    const [metaStatus, setMetaStatus] = useState(null);

    const load = useCallback(async (f = filters) => {
        try {
            const d = await fetchSocialContent(token, {
                package: f.package || undefined, format: f.format || undefined,
                status: f.status || undefined, q: f.q || undefined });
            setItems(d.content);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }, [token, filters]);
    useEffect(() => { load(); fetchMetaStatus(token).then(setMetaStatus).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onChanged = (updated) => {
        if (updated === null) { setItems((xs) => xs.filter((x) => x.id !== active?.id)); return; }
        setItems((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
        setActive(updated);
    };

    const pkgNames = useMemo(() => [...new Map((items || []).map((i) => [i.package_slug, i.package_name])).entries()], [items]);

    return (
        <div data-testid="social-media-manager">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-display font-black text-2xl text-navy flex items-center gap-2"><Share2 className="h-6 w-6 text-saffron" />Social Media</h2>
                <div className="flex rounded-full border border-stone-300 overflow-hidden text-xs font-bold">
                    {[["library", "Content Library"], ["create", "Create New"], ["creative", "AI Art Director"], ["master", "Master Templates"], ["connections", "Connections"]].map(([k, l]) => (
                        <button key={k} data-testid={`social-tab-${k}`} onClick={() => setTab(k)}
                            className={`px-4 py-2 ${tab === k ? "bg-navy text-cream" : "text-stone-500 hover:text-navy"}`}>{l}</button>
                    ))}
                </div>
            </div>

            {tab === "create" && <CreatePanel token={token} onCreated={(c) => { setTab("library"); setActive(c); load(); }} />}
            {tab === "creative" && <ArtDirectorPanel token={token} onSaved={(c) => { setTab("library"); setActive(c); load(); }} />}
            {tab === "master" && <MasterTemplatePanel token={token} onSaved={(c) => { setTab("library"); setActive(c); load(); }} />}
            {tab === "connections" && <ConnectionsPanel token={token} />}
            {tab === "library" && (
                <>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <input data-testid="social-filter-q" placeholder="Search caption / package" value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="rounded-xl border border-stone-300 bg-white px-3.5 py-2 text-sm font-semibold text-navy w-56" />
                        <select data-testid="social-filter-package" value={filters.package}
                            onChange={(e) => setFilters({ ...filters, package: e.target.value })}
                            className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                            <option value="">All packages</option>
                            {pkgNames.map(([s, n]) => <option key={s} value={s}>{n}</option>)}
                        </select>
                        <select data-testid="social-filter-format" value={filters.format}
                            onChange={(e) => setFilters({ ...filters, format: e.target.value })}
                            className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                            <option value="">All formats</option>
                            {Object.entries(FORMAT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <select data-testid="social-filter-status" value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                            <option value="">Any status</option>
                            <option value="ready">Ready to publish</option>
                            <option value="not_ready">Draft</option>
                            {Object.entries(DEST_LABEL).map(([k, l]) => <option key={k} value={k}>Published: {l}</option>)}
                        </select>
                        <button onClick={() => load()} data-testid="social-filter-apply"
                            className="rounded-xl bg-navy text-cream px-4 py-2 text-sm font-bold hover:bg-saffron transition-colors">Apply</button>
                    </div>

                    {items === null ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                    ) : !items.length ? (
                        <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center" data-testid="social-empty">
                            <Share2 className="h-10 w-10 text-stone-300 mx-auto" />
                            <p className="mt-3 font-display font-black text-lg text-navy">No content yet</p>
                            <p className="text-sm text-stone-500 mt-1">Generate your first creatives from the Create New tab.</p>
                            <button onClick={() => setTab("create")} data-testid="social-empty-cta"
                                className="mt-4 rounded-full bg-saffron text-white font-bold px-6 py-2.5 text-sm hover:bg-navy transition-colors">Create content</button>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="social-grid">
                            {items.map((it) => {
                                const firstFmt = it.formats.square ? "square" : Object.keys(it.formats)[0];
                                return (
                                    <button key={it.id} onClick={() => setActive(it)} data-testid={`social-card-${it.id}`}
                                        className="text-left rounded-2xl border border-stone-200 bg-white overflow-hidden hover:border-saffron hover:shadow-lg transition-all">
                                        <div className="h-36 bg-stone-100 relative">
                                            <img src={it.formats[firstFmt].preview_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                            {it.ready && <span className="absolute top-2 left-2 rounded-full bg-green-600 text-white px-2 py-0.5 text-[9px] font-black uppercase">Ready</span>}
                                        </div>
                                        <div className="p-3">
                                            <p className="font-bold text-sm text-navy line-clamp-1">{it.package_name}</p>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {Object.keys(it.formats).map((f) => (
                                                    <span key={f} className="rounded-full bg-cream px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-stone-500">{FORMAT_LABEL[f].split(" ")[0]}</span>
                                                ))}
                                            </div>
                                            <p className="mt-1.5 text-[11px] text-stone-500 line-clamp-2">{it.caption}</p>
                                            <p className="mt-1 text-[10px] text-stone-400">{(it.created_at || "").slice(0, 10)} · by {(it.created_by || "").split("@")[0]}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {active && (
                <DetailModal token={token} item={active} metaStatus={metaStatus}
                    onClose={() => setActive(null)} onChanged={onChanged} />
            )}
        </div>
    );
}
